"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { getHeaderData, getSubmenuData, SubMenuItem } from "@/data/header";

interface ServiceSubmenuProps {
  open: boolean;
  lang?: string;
  onLinkClick?: () => void;
}

const icons = {
  arrowRightMultiple: "/icons/ArrowRightMultiple.svg",
  arrowRightSingle: "/icons/ArrowRightSingle.svg",
  arrowRightSingleHover: "/icons/ArrowRightSingleGreen.svg",
  arrowRightMultipleHover: "/icons/ArrowRightMultipleGreen.svg",
};

export function ServiceSubmenu({
  open,
  lang = "en",
  onLinkClick,
}: ServiceSubmenuProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const servicesList = getSubmenuData("services");
  const navLinks = getHeaderData();
  const servicesNavLink = navLinks.find((item) => item.id === "services");
  const servicesTitle = servicesNavLink?.name;
  const servicesPath = servicesNavLink?.path;

  if (!open) return null;

  const handleItemHover = (itemTitle: string) => {
    const item = servicesList.find((item) => item.title === itemTitle);
    if (item?.hasSubmenu && item?.subItems) {
      setHoveredItem(itemTitle);
    }
    // Don't clear hoveredItem when hovering over items without subitems
  };

  const handleItemLeave = () => {
    // Only clear when leaving the entire submenu container
    setHoveredItem(null);
  };

  const getSubItemsForHoveredItem = () => {
    if (!hoveredItem) return null;
    const item = servicesList.find((item) => item.title === hoveredItem);
    return item?.subItems || null;
  };

  const subItems = getSubItemsForHoveredItem();

  return (
    <>
      <style jsx>{`
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateX(-16px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInSlideItem {
          from {
            opacity: 0;
            transform: translateX(-8px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes iconSlideRight {
          0% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(4px);
          }
          100% {
            transform: translateX(0);
          }
        }
      `}</style>
      <div
        role="menu"
        aria-label="Services"
        className="bg-white outline-none border-t border-gray-100 min-w-[145px]"
        style={{
          boxShadow: "0 30px 60px 0 rgba(0, 0, 0, 0.08)",
        }}
        onMouseLeave={handleItemLeave}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-[62px] py-[52px]">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-start max-w-[1400px] mx-auto">
            {/* Left column: Services */}
            <div className="flex-0 w-full lg:w-1/4">
              <div>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary-800 pr-[44px]">
                  <Link
                    href={`/${lang}${servicesPath}`}
                    className="text-[18px] xl:text-[20px] whitespace-nowrap"
                    onMouseEnter={() => setHoveredIcon("services-main")}
                    onMouseLeave={() => setHoveredIcon(null)}
                  >
                    {servicesTitle}
                  </Link>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="16"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.29478 8.44959L2.4449 14.2496C2.19543 14.4992 1.79563 14.4992 1.59573 14.2496L1.2455 13.8992C1.18686 13.8452 1.14004 13.7797 1.10802 13.7067C1.076 13.6337 1.05947 13.5549 1.05947 13.4752C1.05947 13.3955 1.076 13.3166 1.10802 13.2437C1.14004 13.1707 1.18686 13.1051 1.2455 13.0512L6.34695 7.99999L1.2455 2.99999C1.1866 2.94603 1.13957 2.88039 1.10739 2.80726C1.07521 2.73413 1.05859 2.6551 1.05859 2.57519C1.05859 2.49529 1.07521 2.41626 1.10739 2.34312C1.13957 2.26999 1.1866 2.20436 1.2455 2.15039L1.59732 1.79999C1.79722 1.55039 2.19702 1.55039 2.4465 1.79999L8.34595 7.59999C8.44685 7.72163 8.49771 7.87713 8.4882 8.03491C8.4787 8.1927 8.40954 8.34096 8.29478 8.44959Z"
                      fill="#1A5852"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden lg:block lg:h-[256px] lg:w-px lg:bg-[#DEE1E5]" />

            {/* Middle column: Services items */}
            <div
              className="min-w-[358px] h-[256px] overflow-y-auto pl-[44px]"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#B3B8C2",
              }}
            >
              <ul className="flex flex-col gap-[20px]">
                {servicesList.map((service, index) => (
                  <li key={index}>
                    <div
                      className="group inline-flex items-center gap-2 text-sm text-[14px] xl:text-[16px] font-medium text-[#3D434C] hover:text-[#1A5852] transition-[color] duration-200 cursor-pointer"
                      onMouseEnter={() => {
                        handleItemHover(service.title);
                        setHoveredIcon(service.title);
                      }}
                      onMouseLeave={() => setHoveredIcon(null)}
                    >
                      <Link
                        href={`/${lang}/services${service.href}`}
                        className="flex-1"
                        onClick={(e) => {
                          onLinkClick?.();
                          // Handle smooth scroll for hash links
                          if (service.href.includes("#")) {
                            e.preventDefault();
                            const targetId = service.href.split("#")[1];
                            const targetElement =
                              document.getElementById(targetId);

                            if (targetElement) {
                              // If we're on the same page, scroll smoothly
                              if (
                                window.location.pathname ===
                                `/${lang}/services${service.href.split("#")[0]}`
                              ) {
                                targetElement.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              } else {
                                // If we're navigating to a different page, let Next.js handle it
                                window.location.href = `/${lang}/services${service.href}`;
                              }
                            } else {
                              // Fallback to normal navigation
                              window.location.href = `/${lang}/services${service.href}`;
                            }
                          }
                        }}
                      >
                        {service.title}
                      </Link>
                      {service.hasSubmenu ? (
                        // Double arrow for items that open options on right side
                        <Image
                          src={
                            hoveredIcon === service.title
                              ? icons.arrowRightMultipleHover
                              : icons.arrowRightMultiple
                          }
                          alt="Arrow Right Multiple"
                          width={13}
                          height={14}
                          className={`transition-all duration-200 ${
                            hoveredIcon === service.title
                              ? "animate-[iconSlideRight_0.7s_ease-out_infinite]"
                              : ""
                          }`}
                        />
                      ) : (
                        // Single arrow for clickable links
                        <Image
                          src={
                            hoveredIcon === service.title
                              ? icons.arrowRightSingleHover
                              : icons.arrowRightSingle
                          }
                          alt="Arrow Right Single"
                          width={8}
                          height={14}
                          className={`transition-all duration-200 ${
                            hoveredIcon === service.title
                              ? "animate-[iconSlideRight_0.7s_ease-out_infinite]"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Divider */}
            <div className="hidden lg:block lg:self-stretch lg:w-px lg:bg-[#DEE1E5]" />

            {/* Right column: Dynamic sub-items */}
            <div className="w-[560px]">
              {subItems && Array.isArray(subItems) ? (
                <div
                  key={`submenu-${hoveredItem}`}
                  className="opacity-0 translate-x-[-16px] animate-[fadeInSlide_0.3s_ease-out_forwards]"
                  style={{
                    animation: "fadeInSlide 0.3s ease-out forwards",
                  }}
                >
                  <ul className="space-y-5 pl-[65px]">
                    {subItems.map((subItem, index) => (
                      <li
                        key={`${hoveredItem}-${subItem}-${index}`}
                        className="opacity-0 translate-x-[-8px] transition-all duration-300 ease-out"
                        style={{
                          animationDelay: `${index * 50}ms`,
                          animation: "fadeInSlideItem 0.3s ease-out forwards",
                        }}
                      >
                        <Link
                          href={`/${lang}/services/${encodeURIComponent(
                            subItem
                          )}`}
                          className="group inline-flex items-center gap-2 text-sm text-[14px] xl:text-[16px] font-medium text-[#3D434C] hover:text-[#1A5852] transition-[color] duration-200"
                          onMouseEnter={() => setHoveredIcon(`sub-${subItem}`)}
                          onMouseLeave={() => setHoveredIcon(null)}
                          onClick={(e) => {
                            onLinkClick?.();
                            // Handle smooth scroll for hash links
                            const href = `/${encodeURIComponent(subItem)}`;
                            if (href.includes("#")) {
                              e.preventDefault();
                              const targetId = href.split("#")[1];
                              const targetElement =
                                document.getElementById(targetId);

                              if (targetElement) {
                                // If we're on the same page, scroll smoothly
                                if (
                                  window.location.pathname ===
                                  `/${lang}/services${href.split("#")[0]}`
                                ) {
                                  targetElement.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                  });
                                } else {
                                  // If we're navigating to a different page, let Next.js handle it
                                  window.location.href = `/${lang}/services${href}`;
                                }
                              } else {
                                // Fallback to normal navigation
                                window.location.href = `/${lang}/services${href}`;
                              }
                            }
                          }}
                        >
                          <span>{subItem as string}</span>
                          <Image
                            src={
                              hoveredIcon === `sub-${subItem}`
                                ? icons.arrowRightSingleHover
                                : icons.arrowRightSingle
                            }
                            alt="Arrow Right Single"
                            width={8}
                            height={14}
                            className={`transition-all duration-200 ${
                              hoveredIcon === `sub-${subItem}`
                                ? "animate-[iconSlideRight_0.7s_ease-out_infinite]"
                                : ""
                            }`}
                          />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <span className="text-sm"></span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Mobile Services Submenu Component
interface ServiceMobileSubmenuProps {
  isOpen: boolean;
  lang?: string;
  onLinkClick: () => void;
}

export function ServiceMobileSubmenu({
  isOpen,
  lang = "en",
  onLinkClick,
}: ServiceMobileSubmenuProps) {
  const [isFieldOfServicesOpen, setIsFieldOfServicesOpen] = useState(false);
  const [hoveredMobileIcon, setHoveredMobileIcon] = useState<string | null>(
    null
  );
  const servicesList = getSubmenuData("services");

  return (
    <>
      <style jsx>{`
        @keyframes iconSlideRight {
          0% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(4px);
          }
          100% {
            transform: translateX(0);
          }
        }
      `}</style>
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-[1000px]" : "max-h-0"
        }`}
        style={{
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="ml-4 mt-2 space-y-3 border-l-2 border-gray-200 pl-4">
          {/* Dynamic services list */}
          {servicesList.map((service, index) => (
            <div key={index}>
              {service.hasSubmenu ? (
                // Service with submenu - render as dropdown
                <div>
                  <div className="flex items-center justify-between w-full py-1">
                    <Link
                      href={`/${lang}/services${service.href}`}
                      onClick={(e) => {
                        onLinkClick?.();
                        // Handle smooth scroll for hash links
                        if (service.href.includes("#")) {
                          e.preventDefault();
                          const targetId = service.href.split("#")[1];
                          const targetElement =
                            document.getElementById(targetId);

                          if (targetElement) {
                            // If we're on the same page, scroll smoothly
                            if (
                              window.location.pathname ===
                              `/${lang}/services${service.href.split("#")[0]}`
                            ) {
                              targetElement.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            } else {
                              // If we're navigating to a different page, let Next.js handle it
                              window.location.href = `/${lang}/services${service.href}`;
                            }
                          } else {
                            // Fallback to normal navigation
                            window.location.href = `/${lang}/services${service.href}`;
                          }
                        }
                      }}
                      className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#1A5852] font-medium transition-colors duration-200 flex-1"
                      onMouseEnter={() =>
                        setHoveredMobileIcon(`mobile-${service.title}`)
                      }
                      onMouseLeave={() => setHoveredMobileIcon(null)}
                    >
                      {service.title}
                      <Image
                        src={icons.arrowRightMultiple}
                        alt="Arrow Right Multiple"
                        width={12}
                        height={24}
                        className={`transition-all duration-200 ${
                          hoveredMobileIcon === `mobile-${service.title}`
                            ? "animate-[iconSlideRight_0.7s_ease-out_infinite]"
                            : ""
                        }`}
                      />
                    </Link>
                    <button
                      onClick={() =>
                        setIsFieldOfServicesOpen(!isFieldOfServicesOpen)
                      }
                      className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          isFieldOfServicesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>

                  {/* Nested submenu items */}
                  <div
                    className={`overflow-hidden pr-6 transition-all duration-500 ease-in-out ${
                      isFieldOfServicesOpen ? "max-h-[1000px]" : "max-h-0"
                    }`}
                    style={{
                      transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <div className="ml-4 mt-2 space-y-2 border-l-2 border-gray-100 pl-4">
                      {Array.isArray(service.subItems) &&
                        service.subItems?.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            href={`/${lang}/services/${encodeURIComponent(
                              subItem
                            )}`}
                            onClick={(e) => {
                              onLinkClick?.();
                              // Handle smooth scroll for hash links
                              const href = `/${encodeURIComponent(subItem)}`;
                              if (href.includes("#")) {
                                e.preventDefault();
                                const targetId = href.split("#")[1];
                                const targetElement =
                                  document.getElementById(targetId);

                                if (targetElement) {
                                  // If we're on the same page, scroll smoothly
                                  if (
                                    window.location.pathname ===
                                    `/${lang}/services${href.split("#")[0]}`
                                  ) {
                                    targetElement.scrollIntoView({
                                      behavior: "smooth",
                                      block: "start",
                                    });
                                  } else {
                                    // If we're navigating to a different page, let Next.js handle it
                                    window.location.href = `/${lang}/services${href}`;
                                  }
                                } else {
                                  // Fallback to normal navigation
                                  window.location.href = `/${lang}/services${href}`;
                                }
                              }
                            }}
                            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1A5852] active:text-[#1A5852] transition-colors duration-200 py-1"
                            onMouseEnter={() =>
                              setHoveredMobileIcon(`mobile-sub-${subItem}`)
                            }
                            onMouseLeave={() => setHoveredMobileIcon(null)}
                          >
                            <span className="line-clamp-1">
                              {subItem as string}
                            </span>
                            <Image
                              src={icons.arrowRightSingle}
                              alt="Arrow Right Single"
                              width={7}
                              height={13}
                              className={`transition-all duration-200 ${
                                hoveredMobileIcon === `mobile-sub-${subItem}`
                                  ? "animate-[iconSlideRight_0.7s_ease-out_infinite]"
                                  : ""
                              }`}
                            />
                          </Link>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                // Regular service link
                <Link
                  href={`/${lang}/services${service.href}`}
                  onClick={(e) => {
                    onLinkClick?.();
                    // Handle smooth scroll for hash links
                    if (service.href.includes("#")) {
                      e.preventDefault();
                      const targetId = service.href.split("#")[1];
                      const targetElement = document.getElementById(targetId);

                      if (targetElement) {
                        // If we're on the same page, scroll smoothly
                        if (
                          window.location.pathname ===
                          `/${lang}/services${service.href.split("#")[0]}`
                        ) {
                          targetElement.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        } else {
                          // If we're navigating to a different page, let Next.js handle it
                          window.location.href = `/${lang}/services${service.href}`;
                        }
                      } else {
                        // Fallback to normal navigation
                        window.location.href = `/${lang}/services${service.href}`;
                      }
                    }
                  }}
                  className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#1A5852] active:text-[#1A5852] font-medium transition-colors duration-200 py-1"
                  onMouseEnter={() =>
                    setHoveredMobileIcon(`mobile-regular-${service.title}`)
                  }
                  onMouseLeave={() => setHoveredMobileIcon(null)}
                >
                  {service.title}
                  <Image
                    src={icons.arrowRightSingle}
                    alt="Arrow Right Single"
                    width={7}
                    height={13}
                    className={`transition-all duration-200 ${
                      hoveredMobileIcon === `mobile-regular-${service.title}`
                        ? "animate-[iconSlideRight_0.7s_ease-out_infinite]"
                        : ""
                    }`}
                  />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
